// web/src/lib/api.ts
// Конверт один на весь сервис, поэтому разбор его — тоже один.
export type ApiError = { code: string; message: string; field?: string };
export type ApiReply<T> = { error: ApiError[]; results: T | null };

// Запрос не дошёл до сервера вовсе: сеть недоступна, бэкенд не поднят, дев-прокси
// смотрит не туда. Отдельный код от `BAD_RESPONSE` намеренно — вызывающий код
// обязан отличать «сервер ответил отказом» от «до сервера не достучались».
export const NETWORK_ERROR = "NETWORK_ERROR";
// Сервер ответил, но тело — не JSON: например, дев-прокси отдал HTML своей
// собственной страницы ошибки вместо конверта API.
export const BAD_RESPONSE = "BAD_RESPONSE";

// Тело разобралось как JSON, но это не наш конверт: например, Starlette
// умеет ответить голым `{"detail": "..."}` в обход `api_response` (405 на
// несуществующем методе — это исторический случай, который и завёл эту
// проверку), а дев-прокси иногда отдаёт JSON чужой формы. `as ApiReply<T>`
// сам по себе ничего не проверяет — он просто ГОВОРИТ компилятору поверить
// на слово, поэтому форма проверяется руками до того, как значение уйдёт
// вызывающему коду.
function isApiReply(value: unknown): value is ApiReply<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    Array.isArray((value as { error?: unknown }).error)
  );
}

export async function call<T>(
  path: string,
  body?: unknown,
): Promise<ApiReply<T>> {
  let reply: Response;
  try {
    reply = await fetch(path, {
      method: body === undefined ? "GET" : "POST",
      // Cookie сессии httpOnly: браузер шлёт её сам, но только если попросить.
      credentials: "same-origin",
      // Ключи headers/body не заводятся вовсе, когда тела нет: `exactOptionalPropertyTypes`
      // запрещает записать в них `undefined` явно — не бывает поля со значением
      // "не задано", бывает поле, которого нет.
      ...(body === undefined
        ? {}
        : {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }),
    });
  } catch {
    return {
      error: [
        { code: NETWORK_ERROR, message: "Не удалось связаться с сервером" },
      ],
      results: null,
    };
  }

  let payload: unknown;
  try {
    payload = await reply.json();
  } catch {
    return {
      error: [{ code: BAD_RESPONSE, message: "Сервер вернул не JSON" }],
      results: null,
    };
  }

  if (!isApiReply(payload)) {
    return {
      error: [{ code: BAD_RESPONSE, message: "Сервер вернул не JSON" }],
      results: null,
    };
  }
  return payload as ApiReply<T>;
}

export type Me = { id: number; name: string; short: string };

// Строка раздела «Сток». Предмет строки — товар Ozon, то есть РАЗМЕР:
// 968-M и 968-XL это разные SKU с разными остатками.
export type StockRow = {
  ozon_product_id: number;
  sku: number;
  offer_id: string;
  name: string;
  image: string | null;
  status: string | null;
  // Баркод от кабинета. По нему строится связь с карточкой CRM, поэтому
  // пустая ячейка здесь объясняет пустые короба в соседних.
  barcode: string | null;
  card_id: number | null;
  // Модель CRM: все размеры одного изделия несут один `model_id`. Имя вида
  // «100001 Белый» — код изделия и цвет; в колонку не выносится, живёт
  // подсказкой.
  model_id: number | null;
  model_name: string | null;
  // Остаток на складах МАРКЕТПЛЕЙСА. Ноль здесь при непустых коробах — тот
  // самый кандидат: заказ по такому товару не придёт никогда.
  fbo: number;
  // Что кабинет держит на НАШЕМ складе продавца.
  fbs: number;
  views: number;
  orders: number;
  // Единицы из короба, уже заказанные — ОБЕИМИ площадками разом (задачи
  // склада в статусе «ждёт подбора»). Вычитается из расчёта.
  booked: number;
  // Сколько тех же коробов уже держит за собой Wildberries. Не вычитается —
  // стоит на экране, чтобы двойное обещание было видно глазами.
  promised_wb: number;
  korob_counted: number;
  korob_skipped: number;
  // Короба только по городам, где у нас есть склад FBS.
  korob_working: number;
  // Короба по городам: ключ — номер города CRM СТРОКОЙ (JSON других ключей не
  // знает), значение — штук. Из этого словаря собираются колонки городов.
  korob: Record<string, number>;
  available: number;
  // Свободно на полке Ozon (FBO) по размеру; null — правило полки выключено
  // или полка не прочитана.
  ozon_shelf: number | null;
  // Платье лежит на полке Ozon — правило 14.09.26 отдаёт его на FBS нулём.
  shelf_blocked: boolean;
  fbs_enabled: boolean;
  locations: number[];
};

export type StockLocation = { id: number; name: string; short: string };

// Склад продавца, связанный с городом. Отдельно от справочника городов:
// городов в CRM десятки, а складов два, и прогон возможен только у них.
export type StockWarehouse = {
  location_id: number;
  external_id: number;
  name: string;
};

export type StockReply = {
  rows: StockRow[];
  locations: StockLocation[];
  warehouses: StockWarehouse[];
};

export type StopRun = {
  push_id: number;
  warehouse_id: number;
  status: string;
  positions: number;
  accepted: number;
  rejected: number;
};

export type StopReply = { runs: StopRun[] };

export type PushPreview = {
  warehouse_id: number;
  positions: number;
  units: number;
  zeros: number;
};
